// ─────────────────────────────────────────────────────────
// main.tsx — Application entry point
//
// Phase 2 changes:
//   + Wrapped in QueryClientProvider (TanStack Query)
//   + ReactQueryDevtools added (dev only, tree-shaken in prod)
// ─────────────────────────────────────────────────────────
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools }  from '@tanstack/react-query-devtools'
import './index.css'
import App from './App'
import { attachAuthListener } from '@/services/auth'
import { queryClient }        from '@/lib/queryClient'
import { initSentry }         from '@/lib/sentry'

// 1. Apply saved theme before first render (prevents flash)
const savedTheme = localStorage.getItem('theme') ?? 'light'
document.documentElement.setAttribute('data-theme', savedTheme)

// 2. Initialise Sentry (no-op when VITE_SENTRY_DSN is absent)
initSentry()

// 3. Attach Supabase auth listener immediately
attachAuthListener()

const root = document.getElementById('root')
if (!root) throw new Error('Root element #root not found in index.html')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  </StrictMode>
)
